import { useState } from "react";
import { toast } from "sonner";
import { useAdminStories, adminApi, useAdminMutation, errorMessage, fmt, inputCls } from "@/hooks/useAdmin";
import { AdminHeading, Badge } from "@/components/admin/AdminLayout";

const TABS = ["pending", "published", "rejected", "withdrawn"];
const tone = { pending: "champagne", published: "sage", rejected: "red", withdrawn: "muted" };

function StoryRow({ s }) {
  const [note, setNote] = useState("");
  const moderate = useAdminMutation(adminApi.moderateStory, (d) => toast.success(d.status === "published" ? "Story published." : "Story not published."));
  const m = s.member || {};
  return (
    <div className="border border-forest/15 bg-cream p-5 grid lg:grid-cols-[1fr_300px] gap-6" data-testid={`admin-story-${s.story_id}`} data-status={s.status}>
      <div>
        <div className="flex flex-wrap items-center gap-2 text-xs text-charcoal/50">
          <Badge tone={tone[s.status]}>{s.status}</Badge>
          <span>{m.email}</span><span>·</span><span>Submitted {fmt(s.created_at)}</span>
          {s.consent?.publish && <span>· consent v{s.consent.version} recorded</span>}
        </div>
        <p className="mt-3 text-[10px] uppercase tracking-[0.22em] text-champagne">Exactly as it will appear</p>
        <blockquote className="mt-2 border border-forest/15 bg-ivory p-5">
          <p className="font-serif text-xl text-charcoal/85 leading-relaxed">"{s.body}"</p>
          <p className="mt-4 text-[11px] uppercase tracking-[0.2em] text-moss">{s.display_name}</p>
        </blockquote>
        {s.moderation && <p className="mt-3 text-xs text-charcoal/50">Decision {fmt(s.moderation.at)}{s.moderation.note && ` — ${s.moderation.note}`}</p>}
      </div>
      {s.status === "pending" && (
        <div>
          <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Internal note (optional)" className={inputCls} data-testid={`story-note-${s.story_id}`} />
          <div className="mt-3 flex flex-col gap-2">
            <button onClick={() => moderate.mutate({ id: s.story_id, status: "published", note: note || null }, { onError: (e) => toast.error(errorMessage(e)) })} className="btn btn-primary !py-3" data-testid={`publish-story-${s.story_id}`}>Publish as shown</button>
            <button onClick={() => moderate.mutate({ id: s.story_id, status: "rejected", note: note || null }, { onError: (e) => toast.error(errorMessage(e)) })} className="btn btn-outline !py-3" data-testid={`reject-story-${s.story_id}`}>Do not publish</button>
          </div>
          <p className="mt-3 text-xs text-charcoal/45 font-light">Stories are published verbatim — no edits. Critical experiences are welcome; only reject for policy reasons.</p>
        </div>
      )}
    </div>
  );
}

export default function AdminStories() {
  const [status, setStatus] = useState("pending");
  const { data: stories = [] } = useAdminStories(status);
  return (
    <div data-testid="admin-stories">
      <AdminHeading eyebrow="Moderation" title="Public stories" />
      <div className="flex flex-wrap gap-2 mb-6">
        {TABS.map((t) => <button key={t} onClick={() => setStatus(t)} className={`px-3.5 py-2 text-[10px] uppercase tracking-[0.2em] border transition-colors ${status === t ? "bg-forest text-ivory border-forest" : "border-forest/20 text-charcoal/60 hover:border-forest"}`} data-testid={`story-filter-${t}`}>{t}</button>)}
      </div>
      <div className="space-y-3">
        {stories.length === 0 && <p className="text-sm text-charcoal/50 font-light border border-forest/10 p-8 text-center" data-testid="stories-empty">Nothing {status} right now.</p>}
        {stories.map((s) => <StoryRow key={s.story_id} s={s} />)}
      </div>
    </div>
  );
}
